import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/ui/PageHeader';
import Toggle from '@/components/ui/Toggle';
import Toast from '@/components/ui/Toast';
import FeedbackBoard from '@/components/feedback/FeedbackBoard';
import { aed } from '@/lib/format';
import { useAuth } from '@/lib/auth';
import UsersTab from './components/UsersTab';
import AuditTab from './components/AuditTab';
import IntegrationTab from './components/IntegrationTab';
import RolesTab from './components/RolesTab';
import CostsTab from './components/CostsTab';
import { api, ApiError } from '@/lib/api';
import { usePerms } from '@/lib/perms';

type Tab = 'pricing' | 'costs' | 'users' | 'roles' | 'storefront' | 'feedback' | 'audit' | 'site';

export default function SettingsPage() {
  const { t } = useTranslation();
  const { me, flags } = useAuth();
  const admin = me?.role === 'admin';
  const [params, setParams] = useSearchParams();
  const [tab, setTabState] = useState<Tab>((params.get('tab') as Tab) || 'pricing');
  const setTab = (next: Tab) => {
    setTabState(next);
    const q = new URLSearchParams(params);
    q.set('tab', next);
    setParams(q, { replace: true });
  };
  useEffect(() => {
    const q = params.get('tab') as Tab | null;
    if (q && q !== tab) setTabState(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  const [toastDetail, setToastDetail] = useState<{ detail?: string; tone?: 'success' | 'error' }>({});
  const [markup, setMarkup] = useState(40);
  const [rounding, setRounding] = useState('rounding5');
  const [staleDays, setStaleDays] = useState(7);
  const [guard, setGuard] = useState(true);
  const [minMargin, setMinMargin] = useState(18);
  const [autoMerge, setAutoMerge] = useState(true);
  const [contractGate, setContractGate] = useState(true);
  const [toast, setToast] = useState('');
  const { can } = usePerms();
  const canSettings = can('settings', 'write');
  const [savingRules, setSavingRules] = useState(false);
  // этап 1.3: правила цен читаются и сохраняются на сервере (workspace_settings), не в локальном состоянии макета
  const ROUNDING: Record<string, string> = { '5': 'rounding5', '10': 'rounding10', '1': 'rounding1', none: 'roundingNone' };
  useEffect(() => {
    api
      .get<Record<string, unknown>>('/settings')
      .then((s) => {
        setMarkup(Number(s.default_markup_pct));
        setRounding(ROUNDING[String(s.rounding)] ?? 'rounding5');
        setStaleDays(Number(s.stale_threshold_days));
        setMinMargin(Number(s.min_margin_pct));
        setGuard(Boolean(s.guard_min_resale));
        setAutoMerge(Boolean(s.merge_duplicate_skus));
        setContractGate(Boolean(s.require_signed_contract));
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const saveRules = async () => {
    setSavingRules(true);
    try {
      const back = Object.fromEntries(Object.entries(ROUNDING).map(([k, v]) => [v, k]));
      await api.put('/settings', { values: { default_markup_pct: markup, rounding: back[rounding] ?? '5', stale_threshold_days: staleDays, min_margin_pct: minMargin, guard_min_resale: guard, merge_duplicate_skus: autoMerge, require_signed_contract: contractGate } });
      setToastDetail({});
      setToast(t('set.saved'));
    } catch (e) {
      setToastDetail({ detail: (e as ApiError).code, tone: 'error' });
      setToast(t('ui.error'));
    } finally {
      setSavingRules(false);
    }
  };

  const [showStock, setShowStock] = useState(false);
  const [approval, setApproval] = useState(true);
  const [leadLabel, setLeadLabel] = useState(() => t('sf.availableIn', { weeks: '{weeks}' }));

  const exampleCost = 250.1;
  const tabs: { id: Tab; label: string }[] = [
    { id: 'pricing', label: t('set.tabPricing') },
    { id: 'costs', label: t('set.tabCosts') },
    { id: 'users', label: t('set.tabUsers') },
    { id: 'roles', label: t('set.tabRoles') },
    ...(flags.storefront ? [{ id: 'storefront' as Tab, label: t('set.tabStorefront') }] : []),
    { id: 'feedback', label: t('set.tabFeedback') },
    { id: 'site', label: t('siteint.tab') },
    ...(admin ? [{ id: 'audit' as Tab, label: t('set.tabAudit') }] : []),
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t('set.eyebrow')}
        title={t('set.title')}
        description={t('set.description')}
        actions={
          <button
            type="button"
            onClick={() => (tab === 'pricing' ? void saveRules() : setToast(t('set.saved')))}
            className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-save-line text-[14px]" /> {t('set.save')}
          </button>
        }
      />

      <div className="flex gap-1 border-b border-background-200">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`relative cursor-pointer whitespace-nowrap px-3 py-2.5 text-[12.5px] font-medium transition-colors ${
              tab === item.id ? 'text-primary-700' : 'text-foreground-500 hover:text-foreground-900'
            }`}
          >
            {item.label}
            {tab === item.id && <span className="absolute inset-x-1 -bottom-px h-[2px] rounded-full bg-primary-500" />}
          </button>
        ))}
      </div>

      {tab === 'pricing' && (
        <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div className="flex flex-col gap-4 xl:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-sm font-semibold text-foreground-950">{t('set.rulesTitle')}</h2>
              <p className="mt-0.5 text-[11.5px] leading-4 text-foreground-500">{t('set.rulesSub')}</p>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block min-w-0">
                  <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('set.markup')}</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      max={120}
                      value={markup}
                      onChange={(e) => setMarkup(Number(e.target.value))}
                      className="tnum h-9 w-full rounded-md border border-background-200 bg-background-50 px-2.5 text-[12.5px] outline-none focus:border-primary-400"
                    />
                    <span className="text-[12px] text-foreground-500">%</span>
                  </div>
                </label>
                <label className="block min-w-0">
                  <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('set.rounding')}</span>
                  <select
                    value={rounding}
                    onChange={(e) => setRounding(e.target.value)}
                    className="h-9 w-full cursor-pointer rounded-md border border-background-200 bg-background-50 px-2.5 text-[12.5px] outline-none focus:border-primary-400"
                  >
                    <option value="rounding5">{t('set.rounding5')}</option>
                    <option value="rounding10">{t('set.rounding10')}</option>
                    <option value="rounding1">{t('set.rounding1')}</option>
                    <option value="roundingNone">{t('set.roundingNone')}</option>
                  </select>
                </label>
                <label className="block min-w-0">
                  <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('set.staleThreshold')}</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={60}
                      value={staleDays}
                      onChange={(e) => setStaleDays(Number(e.target.value))}
                      className="tnum h-9 w-full rounded-md border border-background-200 bg-background-50 px-2.5 text-[12.5px] outline-none focus:border-primary-400"
                    />
                    <span className="whitespace-nowrap text-[12px] text-foreground-500">{t('set.days')}</span>
                  </div>
                </label>
                <label className="block min-w-0">
                  <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('set.minMarginGuard')}</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      max={60}
                      value={minMargin}
                      onChange={(e) => setMinMargin(Number(e.target.value))}
                      className="tnum h-9 w-full rounded-md border border-background-200 bg-background-50 px-2.5 text-[12.5px] outline-none focus:border-primary-400"
                    />
                    <span className="text-[12px] text-foreground-500">%</span>
                  </div>
                </label>
              </div>

              <div className="mt-4 space-y-3 border-t border-background-200 pt-4">
                <Toggle checked={guard} onChange={setGuard} label={t('set.guardLabel')} description={t('set.guardDesc')} />
                <Toggle checked={autoMerge} onChange={setAutoMerge} label={t('set.mergeLabel')} description={t('set.mergeDesc')} />
                <Toggle
                  checked={contractGate}
                  onChange={setContractGate}
                  label={t('set.gateLabel')}
                  description={t('set.gateDesc')}
                />
              </div>
              {canSettings && (
                <div className="mt-4 flex justify-end border-t border-background-200 pt-4">
                  <button type="button" disabled={savingRules} onClick={() => void saveRules()} className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-50">
                    <i className="ri-save-line text-[14px]" /> {savingRules ? t('ui.saving') : t('set.save')}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-sm font-semibold text-foreground-950">{t('set.exampleTitle')}</h2>
            <p className="mt-0.5 text-[11px] text-foreground-500">{t('set.exampleSub')}</p>
            <div className="mt-3 space-y-2 text-[12px]">
              {[
                [t('set.exPartnerPrice'), aed(305)],
                [t('set.exAfterDiscount'), aed(exampleCost, 2)],
                [t('set.exMarkup', { value: markup }), `+ ${aed(exampleCost * (markup / 100))}`],
                [t('set.exRounded'), aed(Math.round((exampleCost * (1 + markup / 100)) / 5) * 5)],
                [
                  t('set.exMargin'),
                  `${(((exampleCost * markup) / 100 / (exampleCost * (1 + markup / 100))) * 100).toFixed(1)}%`,
                ],
              ].map(([label, value], i) => (
                <div
                  key={label}
                  className={`flex items-center justify-between ${i === 3 ? 'rounded bg-primary-50 px-2 py-1.5 font-semibold text-primary-800' : ''}`}
                >
                  <span className={i === 3 ? '' : 'text-foreground-500'}>{label}</span>
                  <span className={`tnum ${i === 3 ? '' : 'text-foreground-900'}`}>{value}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 border-t border-background-200 pt-3 text-[10.5px] leading-4 text-foreground-400">
              {t('set.roundingNote', { rounding: t(`set.${rounding}`), margin: minMargin })}
            </p>
          </div>
        </div>
      )}

      {tab === 'users' && (
        <UsersTab
          onToast={(message, detail, tone) => {
            setToastDetail({ detail, tone });
            setToast(message);
          }}
        />
      )}

      {tab === 'feedback' && <FeedbackBoard embedded />}

      {tab === 'costs' && <CostsTab onToast={(m, d, tone) => { setToastDetail({ detail: d, tone }); setToast(m); }} />}
      {tab === 'roles' && <RolesTab onToast={(m, d, tone) => { setToastDetail({ detail: d, tone }); setToast(m); }} />}
      {tab === 'audit' && admin && <AuditTab />}

      {tab === 'site' && (
        <IntegrationTab
          admin={admin}
          onToast={(message, detail, tone) => {
            setToastDetail({ detail, tone });
            setToast(message);
          }}
        />
      )}

      {tab === 'storefront' && (
        <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div className="flex flex-col gap-4 xl:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-sm font-semibold text-foreground-950">{t('set.displayTitle')}</h2>
              <p className="mt-0.5 text-[11.5px] text-foreground-500">{t('set.displaySub')}</p>
              <div className="mt-4 space-y-3">
                <Toggle checked={showStock} onChange={setShowStock} label={t('set.showStockLabel')} description={t('set.showStockDesc')} />
                <Toggle checked={approval} onChange={setApproval} label={t('set.approvalLabel')} description={t('set.approvalDesc')} />
              </div>
              <label className="mt-4 block">
                <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('set.leadLabel')}</span>
                <input
                  value={leadLabel}
                  onChange={(e) => setLeadLabel(e.target.value)}
                  className="h-9 w-full rounded-md border border-background-200 bg-background-50 px-2.5 text-[12.5px] outline-none focus:border-primary-400"
                />
                <span className="mt-1 block text-[10.5px] text-foreground-400">{t('set.leadNote')}</span>
              </label>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block min-w-0">
                  <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('set.currency')}</span>
                  <input
                    defaultValue={t('set.currencyValue')}
                    readOnly
                    className="h-9 w-full rounded-md border border-background-200 bg-background-100 px-2.5 text-[12.5px] text-foreground-600 outline-none"
                  />
                </label>
                <label className="block min-w-0">
                  <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('set.quoteDest')}</span>
                  <input
                    defaultValue={t('set.quoteDestValue')}
                    readOnly
                    className="h-9 w-full rounded-md border border-background-200 bg-background-100 px-2.5 text-[12.5px] text-foreground-600 outline-none"
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <h2 className="text-sm font-semibold text-foreground-950">{t('set.previewLabels')}</h2>
            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-2 rounded border border-background-200 px-2.5 py-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                <p className="text-[11.5px] text-foreground-700">{t('set.inStockDubai')}</p>
              </div>
              <div className="flex items-center gap-2 rounded border border-background-200 px-2.5 py-2">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
                <p className="text-[11.5px] text-foreground-700">{leadLabel.replace('{weeks}', '2')}</p>
              </div>
            </div>
            <p className="mt-3 text-[10.5px] leading-4 text-foreground-400">{t('set.labelsNote')}</p>
          </div>
        </div>
      )}

      {toast && <Toast message={toast} detail={toastDetail.detail} tone={toastDetail.tone} onClose={() => { setToast(''); setToastDetail({}); }} />}
    </div>
  );
}